import {t} from 'i18next';
import React, {useMemo, useState} from 'react';
import {Icon} from '@iconify/react';
import {Item, Menu, RightSlot, Separator} from 'react-contexify';
import {useDispatch, useSelector} from 'react-redux';
import classNames from 'classnames';
import {toast} from 'react-toastify';
import {MENU_IDS} from '../../../constants/menuIds';
import {ISlide} from '../../../incase-domain/slide';
import {createSlideCopyAction, updateSlideWithoutElementsAction} from '../../../store/actions/slide';
import {selectedPresentationSlideCountSelector} from '../../../store/selectors/presentation.selectors';
import DeleteSlideModal from '../../pages/PresEditPage/components/DeleteSlideModal';
import {useRules} from '../../hooks/rulesHook';

export interface SlidePreviewMenuProps {
    slide: ISlide;
    mutateSlide: (cb?: () => void) => void;
}

function slidePreviewProps(props: unknown): SlidePreviewMenuProps | undefined {
    return props as SlidePreviewMenuProps | undefined;
}

function previewSlide(props: unknown): ISlide | undefined {
    return slidePreviewProps(props)?.slide;
}

type SlidePreviewMenuLabelKey =
    | 'contextMenuElement.duplicate'
    | 'contextMenuElement.show'
    | 'contextMenuElement.hide'
    | 'contextMenuElement.removeBookmark'
    | 'contextMenuElement.bookmark'
    | 'contextMenuElement.delete';

type SlidePreviewMenuItemConfig = {
    id: string;
    icon: string;
    labelKey: SlidePreviewMenuLabelKey;
    hidden?: (slide: ISlide | undefined) => boolean;
    disabled?: boolean;
    onClick: (p: SlidePreviewMenuProps) => void;
};

type SlidePreviewMenuEntry = {type: 'item'; config: SlidePreviewMenuItemConfig} | {type: 'separator'; id: string};

export function SlidePreviewContextMenuPortal(): JSX.Element {
    const dispatch = useDispatch();
    const slideCount = useSelector(selectedPresentationSlideCountSelector);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteSlide, setDeleteSlide] = useState<ISlide | null>(null);

    const {checkIfSlideIsUsedInAppliedRules} = useRules();

    const closeDeleteModal = (open: boolean): void => {
        setDeleteModal(open);
        if (!open) setDeleteSlide(null);
    };

    const menuEntries: SlidePreviewMenuEntry[] = useMemo(
        () => [
            {
                type: 'item',
                config: {
                    id: 'duplicate',
                    icon: 'solar:copy-linear',
                    labelKey: 'contextMenuElement.duplicate',
                    onClick: (p) => {
                        p.mutateSlide(() => {
                            dispatch(createSlideCopyAction(p.slide, 1));
                        });
                    },
                },
            },
            {
                type: 'item',
                config: {
                    id: 'show',
                    icon: 'solar:eye-linear',
                    labelKey: 'contextMenuElement.show',
                    hidden: (s) => !s?.hidden,
                    onClick: (p) => {
                        const s = p.slide;
                        dispatch(updateSlideWithoutElementsAction({...s, hidden: false}));
                    },
                },
            },
            {
                type: 'item',
                config: {
                    id: 'hide',
                    icon: 'solar:eye-closed-linear',
                    labelKey: 'contextMenuElement.hide',
                    hidden: (s) => !!s?.hidden,
                    onClick: (p) => {
                        const s = p.slide;
                        dispatch(updateSlideWithoutElementsAction({...s, hidden: true}));
                    },
                },
            },
            {
                type: 'item',
                config: {
                    id: 'removeBookmark',
                    icon: 'solar:star-bold',
                    labelKey: 'contextMenuElement.removeBookmark',
                    hidden: (s) => !s?.onAutoFavorite,
                    onClick: (p) => {
                        const s = p.slide;
                        dispatch(updateSlideWithoutElementsAction({...s, onAutoFavorite: false}));
                    },
                },
            },
            {
                type: 'item',
                config: {
                    id: 'bookmark',
                    icon: 'solar:star-linear',
                    labelKey: 'contextMenuElement.bookmark',
                    hidden: (s) => !!s?.onAutoFavorite,
                    onClick: (p) => {
                        const s = p.slide;
                        dispatch(updateSlideWithoutElementsAction({...s, onAutoFavorite: true}));
                    },
                },
            },
            {type: 'separator', id: 'before-delete'},
            {
                type: 'item',
                config: {
                    id: 'delete',
                    icon: 'solar:trash-bin-minimalistic-linear',
                    labelKey: 'contextMenuElement.delete',
                    disabled: slideCount < 2,
                    onClick: (p) => {
                        if (slideCount < 2) return;
                        if (checkIfSlideIsUsedInAppliedRules(p.slide)) {
                            toast.error(t('toasts.deleteSlideWithAppliedRuleError'));
                            return;
                        }
                        setDeleteSlide(p.slide);
                        setDeleteModal(true);
                    },
                },
            },
        ],
        [dispatch, slideCount, checkIfSlideIsUsedInAppliedRules]
    );

    return (
        <>
            <Menu id={MENU_IDS.SLIDE_PREVIEW}>
                {menuEntries.map((entry) => {
                    if (entry.type === 'separator') {
                        return <Separator key={entry.id} />;
                    }
                    const {config} = entry;
                    const isHidden = config.hidden;
                    return (
                        <Item
                            key={config.id}
                            hidden={isHidden ? ({props}) => isHidden(previewSlide(props)) : undefined}
                            disabled={config.disabled}
                            onClick={({props}) => {
                                const p = slidePreviewProps(props);
                                if (!p) return;
                                config.onClick(p);
                            }}
                        >
                            <Icon className={classNames('contexify_itemContent__icon')} icon={config.icon} />
                            <span className="contexify_itemContent__label">{t(config.labelKey)}</span>
                            <RightSlot>{null}</RightSlot>
                        </Item>
                    );
                })}
            </Menu>

            {deleteModal && deleteSlide != null && (
                <DeleteSlideModal deleteModalOpen={deleteModal} setDeleteModalOpen={closeDeleteModal} slide={deleteSlide} />
            )}
        </>
    );
}
